import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLang } from "../LangContext";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LK = { EN: "en", FR: "fr", NL: "nl" };

export default function WhySport() {
    const { t, lang } = useLang();
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(0);
    const langKey = LK[lang] || "en";

    useEffect(() => {
        axios.get(`${API}/why-sport`).then((r) => setItems(r.data)).catch(() => {});
    }, []);

    if (!items.length) return null;
    const current = items[active];
    const tx = (loc) => loc?.[langKey] || loc?.en || "";

    return (
        <section
            id="why-sport"
            data-testid="why-sport-section"
            className="py-24 md:py-32 px-6 md:px-12 bg-ink relative overflow-hidden"
        >
            <div className="max-w-7xl mx-auto">
                <div className="mb-12 max-w-3xl">
                    <span className="text-xs font-mono uppercase tracking-[0.3em] text-volt">— {t.why.label}</span>
                    <h2 className="font-graffiti text-4xl md:text-6xl tracking-tight mt-6 text-white leading-[0.95]">
                        {t.why.title}
                    </h2>
                    <p className="mt-6 text-zinc-300 text-lg leading-relaxed">{t.why.intro}</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10 border border-white/10 min-h-[520px]">
                    {/* Numbered selector column */}
                    <div className="lg:col-span-4 bg-inksurface flex flex-col">
                        {items.map((p, i) => {
                            const isActive = i === active;
                            return (
                                <button
                                    key={p.id}
                                    data-testid={`why-tab-${i}`}
                                    onMouseEnter={() => setActive(i)}
                                    onClick={() => setActive(i)}
                                    className={`text-left px-6 md:px-8 py-5 md:py-6 border-b border-white/5 last:border-b-0 transition-colors flex items-start gap-4 group ${
                                        isActive ? "bg-volt/[0.06]" : "hover:bg-white/[0.02]"
                                    }`}
                                >
                                    <span
                                        className={`font-mono text-xs mt-1 tracking-[0.2em] transition-colors ${
                                            isActive ? "text-volt" : "text-zinc-500"
                                        }`}
                                    >
                                        {String(p.order).padStart(2, "0")}
                                    </span>
                                    <span
                                        className={`font-graffiti text-xl md:text-2xl leading-tight tracking-tight transition-colors ${
                                            isActive ? "text-white" : "text-zinc-400 group-hover:text-zinc-200"
                                        }`}
                                    >
                                        {tx(p.title)}
                                    </span>
                                    <ArrowRight
                                        className={`ml-auto mt-1 transition-all ${
                                            isActive ? "text-volt translate-x-1 opacity-100" : "text-zinc-600 opacity-0 group-hover:opacity-50"
                                        }`}
                                        size={18}
                                    />
                                </button>
                            );
                        })}
                    </div>

                    {/* Active panel */}
                    <motion.div
                        key={current.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4 }}
                        className="lg:col-span-8 bg-ink p-8 md:p-12 flex flex-col justify-between relative overflow-hidden"
                    >
                        {current.image_url ? (
                            <>
                                <div
                                    className="absolute inset-0 bg-cover bg-center opacity-25"
                                    style={{ backgroundImage: `url(${current.image_url})` }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-br from-ink via-ink/70 to-transparent" />
                            </>
                        ) : null}

                        <div className="relative">
                            <div className="text-xs font-mono uppercase tracking-[0.3em] text-volt mb-5">
                                — {String(current.order).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                            </div>
                            <h3 className="font-graffiti text-3xl md:text-5xl text-white leading-[1.05] tracking-tight">
                                {tx(current.title)}
                            </h3>
                            <p className="mt-6 text-zinc-200 text-lg md:text-xl leading-relaxed max-w-2xl">
                                {tx(current.body)}
                            </p>
                        </div>

                        {current.cta_text ? (
                            <div className="relative mt-8">
                                <a
                                    href={current.cta_link || "#donate"}
                                    data-testid={`why-cta-${active}`}
                                    className="inline-flex items-center gap-2 btn-volt px-6 py-3 text-xs"
                                >
                                    {current.cta_text} <ArrowRight size={14} />
                                </a>
                            </div>
                        ) : null}
                    </motion.div>
                </div>

                <div className="mt-10">
                    <Link
                        to="/why-sport"
                        data-testid="why-read-manifesto"
                        className="btn-ghost inline-flex items-center gap-2 px-6 py-3 text-xs uppercase font-bold tracking-wider"
                    >
                        {t.why.read_full} <ArrowUpRight size={14} />
                    </Link>
                </div>
            </div>
        </section>
    );
}
